import { useState, useMemo } from 'react';
import { Button, CircularProgress, IconButton } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import SecurityIcon from '@mui/icons-material/Security';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// ── Category metadata ─────────────────────────────────────────────────────────
const CATEGORIES = [
    {
        key: 'Master', icon: '🏢', color: '#0d47a1',
        pages: 'Company, Customers, Suppliers, Categories, Products, Banks...',
        claimStart: 0,
    },
    {
        key: 'Transaction', icon: '💳', color: '#6a1b9a',
        pages: 'Sales, Purchases, Services, Gun Deposits, Returns...',
        claimStart: 5,
    },
    {
        key: 'Accounts', icon: '📊', color: '#00695c',
        pages: 'Payments, Receipts, Income & Expense, Journal Entry...',
        claimStart: 10,
    },
    {
        key: 'Reports', icon: '📈', color: '#bf360c',
        pages: 'Stock, Sales Register, Purchase Register, Ledger, GST...',
        claimStart: 15,
    },
    {
        key: 'Administration', icon: '🔐', color: '#e65100',
        pages: 'Users, Roles, Permissions, Change Password...',
        claimStart: 20,
    },
];

const ACTIONS = [
    { key: 'Create', color: 'white', bg: '#2e7d32' },
    { key: 'Read',   color: 'white', bg: '#6a1b9a' },
    { key: 'Edit',   color: 'white', bg: '#1565c0' },
    { key: 'Delete', color: 'white', bg: '#b71c1c' },
    { key: 'Cancel', color: 'white', bg: '#e65100' },
];

const ClaimsManager = ({ claims, onSave, onCancel, isSaving = false }) => {
    const [localClaims, setLocalClaims] = useState(claims);

    // ── Stats ──
    const stats = useMemo(() => {
        const selected = localClaims.filter(c => c.IsSelected).length;
        return { total: localClaims.length, selected, unselected: localClaims.length - selected };
    }, [localClaims]);

    const handleToggle = (index) => {
        const updated = [...localClaims];
        updated[index] = { ...updated[index], IsSelected: !updated[index].IsSelected };
        setLocalClaims(updated);
    };

    const handleAllOff = () => {
        setLocalClaims(prev => prev.map(c => ({ ...c, IsSelected: false })));
    };

    const handleAllOn = () => {
        setLocalClaims(prev => prev.map(c => ({ ...c, IsSelected: true })));
    };

    const handleCategoryToggle = (catIndex) => {
        const start = catIndex * 5;
        const slice = localClaims.slice(start, start + 5);
        const allOn = slice.every(c => c.IsSelected);
        const updated = [...localClaims];
        for (let i = start; i < start + 5 && i < updated.length; i++) {
            updated[i] = { ...updated[i], IsSelected: !allOn };
        }
        setLocalClaims(updated);
    };

    const handleSave = () => { onSave(localClaims); };

    return (
        <div style={{ background: 'white', display: 'flex', flexDirection: 'column', height: '100%', fontFamily: 'Times New Roman' }}>

            {/* ── Header ── */}
            <div style={{
                background: 'linear-gradient(135deg, #2C3E6E 0%, #3d5491 100%)',
                padding: '10px 16px', display: 'flex', alignItems: 'center', gap: '10px',
                borderRadius: '0',
            }}>
                <div style={{ background: 'rgba(255,255,255,0.18)', borderRadius: '7px', padding: '6px', display: 'flex' }}>
                    <SecurityIcon style={{ fontSize: '18px', color: 'white' }} />
                </div>
                <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: 'white', letterSpacing: '0.2px' }}>Manage User Claims</div>
                    <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.75)', marginTop: '1px' }}>
                        {stats.selected}/{stats.total} permissions active
                    </div>
                </div>
                {/* Mini stat badges */}
                <div style={{ display: 'flex', gap: '5px' }}>
                    {[
                        { label: `${stats.total} Total`,    bg: 'rgba(255,255,255,0.18)' },
                        { label: `${stats.selected} Active`, bg: 'rgba(17,153,142,0.5)'  },
                        { label: `${stats.unselected} Off`,  bg: 'rgba(245,87,108,0.4)'  },
                    ].map(s => (
                        <span key={s.label} style={{ background: s.bg, color: 'white', padding: '3px 8px', borderRadius: '5px', fontSize: '10px', fontWeight: '700' }}>
                            {s.label}
                        </span>
                    ))}
                </div>
                <IconButton onClick={onCancel} size="small" style={{ color: 'rgba(255,255,255,0.8)' }}>
                    <CloseIcon style={{ fontSize: '18px' }} />
                </IconButton>
            </div>

            {/* ── Matrix ── */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '14px 16px' }}>

                {/* Column headers row */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: '200px repeat(5, 1fr) 60px',
                    gap: '6px', alignItems: 'center',
                    marginBottom: '8px', padding: '0 8px',
                }}>
                    {/* All Off button */}
                    <div>
                        <button
                            onClick={handleAllOff}
                            style={{
                                background: '#b71c1c', color: 'white', border: 'none',
                                borderRadius: '6px', padding: '4px 10px', fontSize: '10px',
                                fontWeight: '700', cursor: 'pointer', fontFamily: 'Times New Roman',
                            }}
                        >
                            ✕ All Off
                        </button>
                    </div>
                    {ACTIONS.map(a => (
                        <div key={a.key} style={{ textAlign: 'center' }}>
                            <span style={{
                                background: a.bg, color: a.color,
                                padding: '3px 10px', borderRadius: '5px',
                                fontSize: '11px', fontWeight: '700', display: 'inline-block',
                            }}>
                                {a.key}
                            </span>
                        </div>
                    ))}
                    <div style={{ textAlign: 'right', fontSize: '11px', fontWeight: '700', color: '#4a5568' }}>Active</div>
                </div>

                {/* Category rows */}
                {CATEGORIES.map((cat, ci) => {
                    const slice = localClaims.slice(cat.claimStart, cat.claimStart + 5);
                    const activeCount = slice.filter(c => c.IsSelected).length;
                    const allOn = activeCount === 5;

                    return (
                        <div
                            key={cat.key}
                            style={{
                                display: 'grid',
                                gridTemplateColumns: '200px repeat(5, 1fr) 60px',
                                gap: '6px', alignItems: 'center',
                                padding: '10px 8px', marginBottom: '6px',
                                borderRadius: '8px',
                                background: ci % 2 === 0 ? '#fafafa' : 'white',
                                border: '1px solid #f0f0f0',
                                borderLeft: `3px solid ${cat.color}`,
                                transition: 'box-shadow 0.15s',
                            }}
                        >
                            {/* Category info */}
                            <div style={{ cursor: 'pointer' }} onClick={() => handleCategoryToggle(ci)}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                                    <span style={{ fontSize: '14px' }}>{cat.icon}</span>
                                    <span style={{ fontSize: '12px', fontWeight: '700', color: cat.color }}>{cat.key}</span>
                                </div>
                                <div style={{ fontSize: '10px', color: '#a0aec0', lineHeight: '1.4', paddingLeft: '20px' }}>
                                    {cat.pages}
                                </div>
                            </div>

                            {/* Action checkboxes */}
                            {slice.map((claim, si) => (
                                <div key={si} style={{ display: 'flex', justifyContent: 'center' }}>
                                    <div
                                        onClick={() => handleToggle(cat.claimStart + si)}
                                        style={{
                                            width: '28px', height: '28px', borderRadius: '6px',
                                            background: claim.IsSelected ? ACTIONS[si].bg : '#e2e8f0',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            cursor: 'pointer', transition: 'all 0.15s',
                                            boxShadow: claim.IsSelected ? `0 2px 6px ${ACTIONS[si].bg}50` : 'none',
                                        }}
                                    >
                                        {claim.IsSelected && (
                                            <CheckCircleIcon style={{ fontSize: '18px', color: 'white' }} />
                                        )}
                                    </div>
                                </div>
                            ))}

                            {/* Active count */}
                            <div style={{ textAlign: 'right' }}>
                                <span style={{
                                    fontWeight: '700', fontSize: '12px',
                                    color: activeCount === 5 ? '#11998e' : activeCount === 0 ? '#a0aec0' : '#d97706',
                                }}>
                                    {activeCount}/5
                                </span>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* ── Footer ── */}
            <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '10px 16px', borderTop: '1px solid #f0f0f0', gap: '8px',
            }}>
                <button
                    onClick={handleAllOn}
                    style={{
                        background: '#e8f5e9', color: '#2e7d32', border: 'none',
                        borderRadius: '6px', padding: '5px 12px', fontSize: '11px',
                        fontWeight: '700', cursor: 'pointer', fontFamily: 'Times New Roman',
                    }}
                >
                    ✓ All On
                </button>
                <div style={{ display: 'flex', gap: '8px' }}>
                    <Button
                        onClick={onCancel}
                        disabled={isSaving}
                        variant="outlined"
                        size="small"
                        sx={{
                            borderColor: '#d0d7e3', color: '#4a5568',
                            fontSize: '11px', fontWeight: '700',
                            padding: '4px 14px', borderRadius: '7px',
                            minHeight: '30px', textTransform: 'none',
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSave}
                        disabled={isSaving}
                        variant="contained"
                        size="small"
                        startIcon={isSaving
                            ? <CircularProgress size={11} style={{ color: 'white' }} />
                            : <SaveIcon sx={{ fontSize: '13px' }} />
                        }
                        sx={{
                            background: 'linear-gradient(135deg,#2C3E6E,#3d5491)',
                            color: 'white', fontSize: '11px', fontWeight: '700',
                            padding: '4px 14px', borderRadius: '7px',
                            minHeight: '30px', textTransform: 'none',
                            boxShadow: '0 2px 6px rgba(44,62,110,0.3)',
                            '&:hover': { background: 'linear-gradient(135deg,#1a2a4a,#2C3E6E)' },
                        }}
                    >
                        {isSaving ? 'Saving...' : 'Save Changes'}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default ClaimsManager;
